import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, FileText, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { EDUCATIONAL_ACK, PRIVACY_ACK } from "@/data/legal";

const EXTRA_SLOTS = [
  { key: "plans", label: "Approved development plans (optional)" },
  { key: "modification", label: "Modified development consent (optional)" },
  { key: "correspondence", label: "Council correspondence (optional)" },
  { key: "reports", label: "Other reports or approvals (optional)" },
];

const DaUpload = () => {
  const [params] = useSearchParams();
  const [consent, setConsent] = useState(null);
  const [extras, setExtras] = useState({});
  const [meta, setMeta] = useState({ council: "", address: "", da_ref: "", modified: "no" });
  const [stage, setStage] = useState({ cc_obtained: false, pc_appointed: false, commenced: false });
  const [phase, setPhase] = useState(null); // null | "uploading"
  const [acks, setAcks] = useState({ educational: false, privacy: false });

  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.info("Payment was cancelled — your upload wasn't analysed and you weren't charged.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!consent) return toast.error("Please upload your development consent PDF first");
    if (!acks.educational || !acks.privacy) {
      return toast.error("Please read and tick both acknowledgements before continuing.");
    }
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("consent", consent);
      fd.append("meta", JSON.stringify({ ...meta, ...stage }));
      fd.append("acks", JSON.stringify(acks));
      fd.append("origin_url", window.location.origin);
      Object.values(extras).forEach((f) => f && fd.append("extras", f));
      const { data } = await api.post("/roadmap/analyse", fd, { timeout: 90000 });
      window.location.href = data.checkout_url;
    } catch (e) {
      setPhase(null);
      toast.error(e.response?.data?.detail || "Upload failed — please try again.");
    }
  };

  return (
    <div data-testid="da-upload-page">
      <Seo title="Upload Your DA Consent" noindex />
      <section className="bg-sand-light/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/da-approved" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> After Your DA Is Approved
          </Link>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-teal tracking-tight">Upload your consent</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed">
            The official PDF council sent you — including all conditions of consent. Scanned PDFs
            without selectable text can't be read by our pilot yet.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <FadeIn>
            <div className="bg-sand-light/60 border border-cloud p-7" data-testid="da-upload-notice">
              <p className="font-serif text-lg text-teal">Before you upload your development consent</p>
              <div className="mt-3 space-y-2.5 text-sm text-charcoal/75 leading-relaxed">
                <p>
                  This tool uses AI to help organise and explain information contained in your
                  development consent. Your personalised roadmap is designed to help you understand
                  the document, identify potential next steps and prepare questions for council,
                  your certifier or another qualified professional.
                </p>
                <p>
                  It is not a professional planning assessment, certification of compliance or
                  official interpretation of your conditions of consent. AI-generated information
                  may be incomplete or inaccurate — you must confirm applicable requirements with
                  the relevant authority or professional before taking action.
                </p>
                <p className="text-xs">
                  Please review our{" "}
                  <Link to="/legal/privacy" className="text-teal underline underline-offset-4">Privacy Policy</Link>{" "}
                  and{" "}
                  <Link to="/legal/document-upload" className="text-teal underline underline-offset-4">Document Upload &amp; Data Handling Notice</Link>{" "}
                  to understand how your uploaded information will be handled.
                </p>
              </div>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="bg-white border border-cloud p-7">
              <p className="font-serif text-lg text-teal">Your development consent *</p>
              <label
                data-testid="da-consent-dropzone"
                className={`mt-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-10 cursor-pointer transition-colors ${
                  consent ? "border-teal bg-sand-light/40" : "border-cloud hover:border-teal"
                }`}
              >
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  data-testid="da-consent-input"
                  onChange={(e) => setConsent(e.target.files?.[0] || null)}
                />
                <FileText className="w-8 h-8 text-teal/50" />
                <span className="text-sm text-charcoal/75">
                  {consent ? consent.name : "Choose your consent PDF (max 25 MB)"}
                </span>
                {consent && <span className="text-xs text-teal font-mono">{(consent.size / 1024 / 1024).toFixed(1)} MB</span>}
              </label>

              <div className="mt-6 space-y-3">
                {EXTRA_SLOTS.map((s) => (
                  <div key={s.key} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-charcoal/70">{s.label}</span>
                    {extras[s.key] ? (
                      <span className="flex items-center gap-2 text-xs text-teal font-mono">
                        {extras[s.key].name.slice(0, 32)}
                        <button onClick={() => setExtras((x) => ({ ...x, [s.key]: null }))} aria-label="Remove file" data-testid={`da-extra-remove-${s.key}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ) : (
                      <label className="text-xs font-mono uppercase tracking-[0.12em] text-teal cursor-pointer hover:underline underline-offset-4">
                        Add file
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          data-testid={`da-extra-input-${s.key}`}
                          onChange={(e) => setExtras((x) => ({ ...x, [s.key]: e.target.files?.[0] || null }))}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.05}>
            <div className="bg-white border border-cloud p-7 space-y-5">
              <p className="font-serif text-lg text-teal">About your approval</p>
              {[
                ["council", "Your council", "e.g. Shoalhaven City Council"],
                ["address", "Property address", "Street, suburb, postcode"],
                ["da_ref", "DA reference", "e.g. DA2024/1234"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-xs font-mono uppercase tracking-[0.12em] text-charcoal/60">{label}</label>
                  <input
                    value={meta[k]}
                    onChange={(e) => setMeta((m) => ({ ...m, [k]: e.target.value }))}
                    placeholder={ph}
                    data-testid={`da-meta-${k}`}
                    className="mt-1.5 w-full bg-ivory border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.12em] text-charcoal/60">Has the consent been modified?</label>
                <div className="mt-2 flex gap-2">
                  {["no", "yes", "not sure"].map((o) => (
                    <button
                      key={o}
                      onClick={() => setMeta((m) => ({ ...m, modified: o }))}
                      data-testid={`da-modified-${o.replace(" ", "-")}`}
                      className={`text-xs px-4 py-2.5 border rounded-full transition-colors ${
                        meta.modified === o ? "bg-teal text-ivory border-teal" : "bg-white border-cloud text-charcoal/70 hover:border-teal"
                      }`}
                    >
                      {o[0].toUpperCase() + o.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.12em] text-charcoal/60">Where are you up to? (tick any)</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    ["cc_obtained", "Construction Certificate obtained"],
                    ["pc_appointed", "Principal Certifier appointed"],
                    ["commenced", "Works already started"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setStage((s) => ({ ...s, [k]: !s[k] }))}
                      data-testid={`da-stage-${k}`}
                      className={`text-xs px-4 py-2.5 border rounded-full transition-colors ${
                        stage[k] ? "bg-teal text-ivory border-teal" : "bg-white border-cloud text-charcoal/70 hover:border-teal"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="bg-white border border-cloud p-7 space-y-4" data-testid="da-acknowledgements">
              <p className="font-serif text-lg text-teal">Before we begin — two quick acknowledgements</p>
              {[
                ["educational", EDUCATIONAL_ACK, "da-ack-educational"],
                ["privacy", PRIVACY_ACK, "da-ack-privacy"],
              ].map(([k, text, testid]) => (
                <label key={k} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={acks[k]}
                    onChange={(e) => setAcks((a) => ({ ...a, [k]: e.target.checked }))}
                    data-testid={testid}
                    className="mt-1 w-4 h-4 shrink-0 accent-teal"
                  />
                  <span className="text-xs text-charcoal/75 leading-relaxed group-hover:text-charcoal transition-colors">
                    {text}
                  </span>
                </label>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <button
              onClick={submit}
              disabled={!!phase}
              data-testid="da-analyse-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-4 rounded-full hover:bg-teal-dark transition-colors disabled:opacity-60"
            >
              {phase ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {phase ? "Uploading securely…" : "Pay A$69 & Build My Roadmap"}
            </button>
            <p className="mt-2 text-center text-xs font-mono uppercase tracking-[0.12em] text-charcoal/55">
              GST included · secure Stripe checkout
            </p>
            <p className="mt-4 flex items-start gap-2 text-xs text-charcoal/55 leading-relaxed">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              Your documents are stored securely, accessible only to PLAN FIRST NSW, and never used
              to train AI models. You can ask us to delete your documents at any time via
              hello@planfirstnsw.com.au. If we can't read your consent reliably, you receive a full refund.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default DaUpload;
